import React, { useState, useEffect, useRef } from 'react';
import { base44 } from '@/api/base44Client';
import { Link, useNavigate } from 'react-router-dom';
import { TrendingUp, CheckCircle, AlertTriangle, Clock, FileText, ChevronRight, Award, Download, Building2 } from 'lucide-react';
import MaturityChart from '@/components/dashboard/MaturityChart';
import ThemeHeatmap from '@/components/dashboard/ThemeHeatmap';
import LevelProgressTracker from '@/components/dashboard/LevelProgressTracker';
import SectorBenchmark from '@/components/dashboard/SectorBenchmark';
import AISummary from '@/components/dashboard/AISummary';
import { useOrganisation } from '@/lib/OrganisationContext';
import { operationMatches } from '@/lib/operationGuard';

const LEVEL_CONFIG = {
  0: { label: 'Not Started', color: 'text-muted-foreground', bg: 'bg-muted', badge: 'bg-slate-200 text-slate-700' },
  1: { label: 'Committed', color: 'text-slate-600', bg: 'bg-slate-100', badge: 'bg-slate-600 text-white' },
  2: { label: 'Employer', color: 'text-teal-700', bg: 'bg-teal-50', badge: 'bg-teal-600 text-white' },
  3: { label: 'Leader', color: 'text-amber-700', bg: 'bg-amber-50', badge: 'bg-amber-500 text-white' },
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { selectedOrg } = useOrganisation();
  const [latestResult, setLatestResult] = useState(null);
  const [currentDraft, setCurrentDraft] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [startingAssessment, setStartingAssessment] = useState(false);
  const [assessmentError, setAssessmentError] = useState('');
  const loadGeneration = useRef(0);
  const assessmentAction = useRef(0);
  const selectedOrgRef = useRef(selectedOrg?.id);

  useEffect(() => () => { loadGeneration.current += 1; assessmentAction.current += 1; }, []);

  useEffect(() => {
    selectedOrgRef.current = selectedOrg?.id;
    assessmentAction.current += 1;
    setStartingAssessment(false); setAssessmentError('');
  }, [selectedOrg?.id]);

  useEffect(() => {
    loadData();
  }, [selectedOrg?.id]);

  // Refresh data when returning to this page
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadData();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Realtime: refresh when other users create/update assessments, tasks, or evidence
  useEffect(() => {
    const unsubAssessment = base44.entities.Assessment.subscribe(() => loadData());
    const unsubTask = base44.entities.RoadmapTask.subscribe(() => loadData());
    const unsubEvidence = base44.entities.EvidenceDocument.subscribe(() => loadData());
    return () => {
      unsubAssessment();
      unsubTask();
      unsubEvidence();
    };
  }, [selectedOrg?.id]);

  async function handleExport() {
    setExporting(true);
    try {
      const response = await base44.functions.invoke('exportMaturityReport', {});
      // response.data is a Blob/ArrayBuffer from axios
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DC-Maturity-Report-${new Date().toISOString().slice(0,10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed', e);
    } finally {
      setExporting(false);
    }
  }

  async function handleAssessmentAction() {
    if (currentDraft || !latestResult) { navigate('/assessment'); return; }
    const token = ++assessmentAction.current; const orgId = selectedOrgRef.current;
    setStartingAssessment(true); setAssessmentError('');
    try {
      await base44.functions.invoke('startReassessment', {});
      if (operationMatches(token, assessmentAction.current, orgId, selectedOrgRef.current)) navigate('/assessment');
    } catch (error) {
      if (operationMatches(token, assessmentAction.current, orgId, selectedOrgRef.current)) setAssessmentError(error?.message || 'Unable to start reassessment.');
    } finally {
      if (operationMatches(token, assessmentAction.current, orgId, selectedOrgRef.current)) setStartingAssessment(false);
    }
  }

  async function loadData() {
    const generation = ++loadGeneration.current;
    try {
      const orgId = selectedOrg?.id;
      const [results, allEvidence, currentOrganisation] = await Promise.all([
        base44.entities.Assessment.filter(orgId ? { organisation_id: orgId } : {}, '-created_date', 100),
        base44.entities.EvidenceDocument.filter(orgId ? { organisation_id: orgId } : {}, '-created_date', 200),
        orgId ? base44.entities.Organisation.get(orgId) : Promise.resolve(null),
      ]);
      if (generation !== loadGeneration.current) return;
      
      // Load all tasks and filter client-side to ensure we capture all completed work
      const allTasks = await base44.entities.RoadmapTask.list('-created_date', 500);
      if (generation !== loadGeneration.current) return;
      // Org-specific tasks reflect this company's actual progress.
      // New companies (not yet assessed) see default tasks as "not started"
      // so completion metrics read 0 instead of inheriting other orgs' progress.
      let tasksToUse = allTasks.filter(t => t.organisation_id === orgId);
      if (tasksToUse.length === 0) {
        tasksToUse = allTasks
          .filter(t => t.is_default === true)
          .map(t => ({ ...t, status: 'not_started' }));
      }
      // If still empty, use all tasks as fallback
      if (tasksToUse.length === 0) {
        tasksToUse = allTasks;
      }
      
      const draft = results.find(result => result.status === 'in_progress') || null;
      const submitted = results.find(result => result.status === 'completed') || null;
      const submittedHistory = results.filter(result => result.status === 'completed');
      setAssessments(submittedHistory.length ? submittedHistory : draft ? [draft] : []);
      setLatestResult(submitted || draft);
      setCurrentDraft(draft);
      setTasks(tasksToUse);
      setEvidence(allEvidence);
      setOrg(currentOrganisation);
    } catch (e) {
      if (generation === loadGeneration.current) console.error(e);
    } finally {
      if (generation === loadGeneration.current) setLoading(false);
    }
  }

  const currentLevel = latestResult?.current_level ?? 0;
  const levelCfg = LEVEL_CONFIG[currentLevel];

  // Task stats per level
  const tasksByLevel = [1, 2, 3].map(l => {
    const levelTasks = tasks.filter(t => t.level === l);
    const done = levelTasks.filter(t => t.status === 'complete').length;
    const pct = levelTasks.length ? Math.round((done / levelTasks.length) * 100) : 0;
    return { level: l, total: levelTasks.length, done, pct };
  });

  const overdueTasks = tasks.filter(t => t.status !== 'complete' && t.due_date && new Date(t.due_date) < new Date());
  const pendingTasks = tasks.filter(t => t.status !== 'complete').slice(0, 5);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" role="status" aria-live="polite">
        <div className="w-8 h-8 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin" aria-label="Loading dashboard" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 max-w-6xl mx-auto">
      <div className="sr-only" aria-live="polite" aria-atomic="true">Dashboard loaded</div>

      {/* Export button */}
      <div className="flex flex-wrap justify-between gap-2">
        {selectedOrg?.is_provider ? <Link
          to="/admin/customers/new"
          className="flex min-h-11 items-center gap-2 rounded-lg bg-teal-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-teal-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-400 sm:px-4 sm:text-sm"
        >
          <Building2 className="h-4 w-4" aria-hidden="true" />
          <span>New customer in console</span>
        </Link> : <span />}
        <div className="flex gap-2">
          <button
            onClick={loadData}
            className="flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg border border-border bg-card text-xs sm:text-sm font-medium text-foreground hover:bg-muted transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
          >
            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span className="hidden sm:inline">Refresh</span>
            <span className="sm:hidden">Refresh</span>
          </button>
          <button
            onClick={handleExport}
            disabled={exporting || !latestResult}
            className="flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg border border-border bg-card text-xs sm:text-sm font-medium text-foreground hover:bg-muted transition-colors disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
          >
            <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" aria-hidden="true" />
            <span className="hidden sm:inline">{exporting ? 'Generating…' : 'Export PDF'}</span>
            <span className="sm:hidden">{exporting ? 'Generating…' : 'Export'}</span>
          </button>
        </div>
      </div>

      {/* Status banner */}
      {currentDraft && <div className="rounded-xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900" role="status"><p className="font-semibold">Assessment draft in progress</p><p className="mt-1">Current draft score: {currentDraft.maturity_score}%. Continue the draft to review and submit your answers.{latestResult?.status === 'completed' ? ` Your latest submitted score remains ${latestResult.maturity_score}%.` : ''}</p></div>}
      <div className={`rounded-xl sm:rounded-2xl border border-border p-4 sm:p-6 ${levelCfg.bg}`}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl flex items-center justify-center ${levelCfg.badge}`} aria-hidden="true">
              <Award className="w-5 h-5 sm:w-7 sm:h-7" />
            </div>
            <div>
              <p className="text-[10px] sm:text-xs font-medium uppercase tracking-wide text-muted-foreground">{latestResult?.status === 'completed' ? 'Submitted self-assessment' : latestResult ? 'Draft self-assessment' : 'Self-assessed readiness'}</p>
              <h2 className="text-base sm:text-2xl font-bold text-foreground">
                {currentLevel === 0 ? (latestResult ? 'No level fully achieved yet' : 'Not Yet Assessed') : `Level ${currentLevel} — ${levelCfg.label}`}
              </h2>
              {latestResult && (
                <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5">Score: <strong>{latestResult.maturity_score}%</strong></p>
              )}
              {org && <p className="mt-1 text-xs text-muted-foreground">DI-authorised organisation level: {org.current_dc_level ?? 0}</p>}
            </div>
          </div>
          <button type="button"
            onClick={handleAssessmentAction}
            disabled={startingAssessment}
            className="inline-flex items-center gap-1.5 sm:gap-2 px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg bg-teal-600 text-white text-xs sm:text-sm font-semibold hover:bg-teal-700 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-400 shrink-0"
          >
            {startingAssessment ? 'Starting reassessment…' : currentDraft ? 'Continue draft' : latestResult ? 'Re-assess' : 'Start Assessment'}
            <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" aria-hidden="true" />
          </button>
        </div>
        {assessmentError && <p role="alert" className="mt-3 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800">{assessmentError}</p>}
      </div>

      {/* AI Summary */}
      {latestResult && (
        <AISummary
          latestResult={latestResult}
          tasks={tasks}
          evidence={evidence}
          currentLevel={currentLevel}
        />
      )}

      {/* Metric cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <MetricCard icon={<CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-teal-600" aria-hidden="true" />} label="Tasks Complete" value={`${tasks.filter(t => t.status === 'complete').length} / ${tasks.length}`} />
        <MetricCard icon={<Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-500" aria-hidden="true" />} label="Overdue Tasks" value={overdueTasks.length} alert={overdueTasks.length > 0} />
        <MetricCard icon={<FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-teal-600" aria-hidden="true" />} label="Evidence Docs" value={evidence.length} />
        <MetricCard icon={<TrendingUp className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-teal-600" aria-hidden="true" />} label="Maturity Score" value={latestResult ? `${latestResult.maturity_score}%` : 'N/A'} />
      </div>

      {/* Maturity chart */}
      <MaturityChart
        assessments={assessments}
        levelScores={{
          level1_score: latestResult?.level1_score,
          level2_score: latestResult?.level2_score,
          level3_score: latestResult?.level3_score,
        }}
      />

      {/* Sector benchmark comparison */}
      <SectorBenchmark org={org} latestResult={latestResult} />

      {/* Level progress tracker */}
      <LevelProgressTracker
        currentLevel={currentLevel}
        maturityScore={latestResult?.maturity_score}
        levelScores={{
          1: latestResult?.level1_score,
          2: latestResult?.level2_score,
          3: latestResult?.level3_score,
        }}
      />

      {/* Theme heatmap */}
      <ThemeHeatmap assessments={assessments} currentLevel={currentLevel} organisationId={selectedOrg?.id} />

      {/* Level progress */}
      <section aria-labelledby="level-progress-heading">
        <h2 id="level-progress-heading" className="text-xs sm:text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2 sm:mb-3">Roadmap Progress by Level</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          {tasksByLevel.map(({ level, total, done, pct }) => {
            const cfg = LEVEL_CONFIG[level];
            return (
              <div key={level} className="bg-card border border-border rounded-xl p-3 sm:p-4 shadow-sm">
                <div className="flex items-center justify-between mb-2 sm:mb-3">
                  <span className={`text-[10px] sm:text-xs font-bold px-1.5 py-0.5 rounded-full ${cfg.badge}`}>L{level} — {cfg.label}</span>
                  <span className="text-xs sm:text-sm font-semibold text-foreground">{pct}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-1.5 sm:h-2" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={`Level ${level} progress: ${pct}%`}>
                  <div
                    className={`h-1.5 sm:h-2 rounded-full transition-all ${level === 1 ? 'bg-slate-500' : level === 2 ? 'bg-teal-500' : 'bg-amber-500'}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <p className="text-[10px] sm:text-xs text-muted-foreground mt-1.5 sm:mt-2">{done} of {total}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Quick actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Pending tasks */}
        <section aria-labelledby="pending-tasks-heading" className="bg-card border border-border rounded-xl p-3 sm:p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h2 id="pending-tasks-heading" className="text-xs sm:text-sm font-semibold text-foreground">Outstanding Tasks</h2>
            <Link to="/roadmap" className="text-[10px] sm:text-xs text-teal-600 hover:text-teal-700 font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring rounded">View all</Link>
          </div>
          {pendingTasks.length === 0 ? (
            <p className="text-xs sm:text-sm text-muted-foreground">All tasks complete!</p>
          ) : (
            <ul className="space-y-1.5 sm:space-y-2">
              {pendingTasks.map(task => (
                <li key={task.id} className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                  <StatusDot status={task.status} />
                  <span className="flex-1 text-foreground truncate">{task.title}</span>
                  <span className={`text-[10px] sm:text-xs px-1 py-0.5 rounded font-medium ${LEVEL_CONFIG[task.level]?.badge}`}>L{task.level}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Evidence gaps */}
        <section aria-labelledby="evidence-gaps-heading" className="bg-card border border-border rounded-xl p-3 sm:p-5 shadow-sm">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <h2 id="evidence-gaps-heading" className="text-xs sm:text-sm font-semibold text-foreground">Evidence Overview</h2>
            <Link to="/evidence-vault" className="text-[10px] sm:text-xs text-teal-600 hover:text-teal-700 font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring rounded">View vault</Link>
          </div>
          {evidence.length === 0 ? (
            <p className="text-xs sm:text-sm text-muted-foreground">No evidence uploaded yet.</p>
          ) : (
            <div className="space-y-1.5 sm:space-y-2">
              {[1, 2, 3].map(l => {
                const docs = evidence.filter(e => e.linked_level === l);
                return (
                  <div key={l} className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                    <span className={`text-[10px] sm:text-xs font-semibold px-1 py-0.5 rounded ${LEVEL_CONFIG[l].badge}`}>L{l}</span>
                    <span className="text-foreground">{docs.length} doc{docs.length !== 1 ? 's' : ''}</span>
                    {docs.length === 0 && <span className="text-[10px] sm:text-xs text-amber-600 font-medium ml-auto flex items-center gap-1"><AlertTriangle className="w-2.5 h-2.5 sm:w-3 sm:h-3" aria-hidden="true" />Gap</span>}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

    </div>
  );
}

function MetricCard({ icon, label, value, alert }) {
  return (
    <div className={`bg-card border rounded-xl p-3 sm:p-4 shadow-sm ${alert ? 'border-amber-200' : 'border-border'}`}>
      <div className="flex items-center gap-1.5 sm:gap-2 mb-2">
        {icon}
        <span className="text-[10px] sm:text-xs text-muted-foreground font-medium">{label}</span>
      </div>
      <p className={`text-base sm:text-lg font-bold ${alert ? 'text-amber-600' : 'text-foreground'}`}>{value}</p>
    </div>
  );
}

function StatusDot({ status }) {
  const map = {
    not_started: { color: 'bg-slate-300', label: 'Not started' },
    in_progress: { color: 'bg-amber-400', label: 'In progress' },
    complete: { color: 'bg-teal-500', label: 'Complete' },
  };
  const { color, label } = map[status] || map.not_started;
  return <span role="img" className={`w-2 h-2 rounded-full shrink-0 ${color}`} aria-label={label} />;
}
